import styled from "styled-components";
import React, { useEffect, useState } from "react";
import { CheckboxStyles } from "../../types";
import Icon from "../Icon/Icon";
import { TickSolid } from "../../assets/icons";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 13px;
`;

const CheckboxWithLabelContainer = styled.div<{
  boldStyle?: boolean;
  styles?: CheckboxStyles;
}>`
  display: flex;
  align-items: center;
  font-weight: ${({ boldStyle }) => (boldStyle ? "bold" : "normal")};
  color: ${({ styles }) => styles?.OverallGeneralTextColour};

  a {
    color: ${({ styles }) => styles?.OverallGeneralTextColour};
  }
`;

const CustomCheckbox = styled.div<{
  isChecked: boolean;
  isErrored?: boolean;
  styles?: CheckboxStyles;
}>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border: 2px solid
    ${({ isChecked, isErrored, styles }) => {
      if (isChecked) return styles?.OverallBookButtonColour;
      return isErrored
        ? styles?.DropdownGeneralValidationErrorColour
        : "#828d98";
    }};
  border-radius: 4px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ isChecked, styles }) =>
    isChecked ? styles?.OverallBookButtonColour : "#ffffff"};
  color: #ffffff;
  cursor: pointer;

  &:focus {
    outline: 1px solid #000;
  }
`;

const ErrorMessage = styled.div<{
  colour?: string;
}>`
  color: ${({ colour }) => colour};
`;

export type CheckboxWithLabelProps = {
  children: React.ReactElement<HTMLLabelElement>;
  isChecked?: boolean;
  boldStyle?: boolean;
  onChecked: (checked: boolean) => void;
  styles?: CheckboxStyles;
  errorMessage?: string;
  dataId?: string;
  forceChecked?: boolean | null;
};

export const CheckboxWithLabel: React.FC<CheckboxWithLabelProps> = ({
  children,
  isChecked = false,
  onChecked,
  boldStyle,
  styles = {
    OverallBookButtonColour: "#007a7a",
    OverallGeneralTextColour: "#202227",
    DropdownGeneralValidationErrorColour: "#D82C0D",
  },
  errorMessage,
  dataId,
  forceChecked = null,
}) => {
  const [checked, setChecked] = useState(isChecked);
  const checkboxId = `checkbox-${Math.random().toString(36).substr(2, 9)}`;
  const labelId = `${checkboxId}-label`;

  useEffect(() => {
    if (forceChecked === null) return;
    setChecked(forceChecked);
    onChecked(forceChecked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceChecked]);

  const handleToggle = () => {
    const newChecked = !checked;
    setChecked(newChecked);
    onChecked(newChecked);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <Container data-id={dataId + "-container"}>
      <CheckboxWithLabelContainer boldStyle={boldStyle} styles={styles}>
        <CustomCheckbox
          role="checkbox"
          id={checkboxId}
          isChecked={checked}
          aria-checked={checked}
          aria-labelledby={labelId}
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          styles={styles}
          isErrored={!!errorMessage}
          data-id={dataId + "-checkbox" + (checked ? "-checked" : "")}
        >
          {checked && <Icon icon={TickSolid} />}
        </CustomCheckbox>
        <label id={labelId} htmlFor={checkboxId}>
          {children}
        </label>
      </CheckboxWithLabelContainer>
      {errorMessage && (
        <ErrorMessage colour={styles.DropdownGeneralValidationErrorColour}>
          {errorMessage}
        </ErrorMessage>
      )}
    </Container>
  );
};

export default CheckboxWithLabel;
